"use client";

import { trackEvent } from "@pex/analytics/client";
import { usePaystackCheckout } from "@pex/billing/client";
import {
  type TurnstileErrorReason,
  TurnstileWidget,
} from "@pex/turnstile/client";
import { Button, Checkbox } from "@pex/ui-web";
import { Check } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { prepareWebsiteCheckout } from "@/app/(main)/partner/getting-started/actions";
import { formatRand } from "@/lib/plan-copy";

const BRIDGE_URL = process.env.NEXT_PUBLIC_BRIDGE_URL ?? "";
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

// Shown when the bot check cannot produce a token. It names the most common
// cause (private browsing / content blockers) because the user can act on that;
// "something went wrong" would leave them staring at a disabled button.
const TURNSTILE_ERROR_MESSAGE =
  "The security check couldn't complete. This is usually caused by private browsing or a content blocker — try again in a normal browser window, or contact support if it keeps happening.";

function intervalLabel(interval: string): string {
  return interval === "annually" ? "/ year" : "/ month";
}

/**
 * The consent + payment step (D4). Mirrors the old pricing-page dialog:
 * terms must be accepted before every Paystack open, the popup uses a fresh
 * instance per open (handled inside usePaystackCheckout), and success hops
 * cross-origin to Bridge's org-setup step.
 */
export function CheckoutForm({
  amount,
  email,
  features,
  interval,
  planName,
  processorPlanId,
}: {
  amount: number;
  email: string;
  features: readonly string[];
  interval: string;
  planName: string;
  processorPlanId: string;
}) {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Null until Turnstile solves, and back to null when the token expires or
  // fails. The server rejects a missing token, so gating the button on it is
  // only a UX nicety — the real enforcement is in prepareWebsiteCheckout.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Distinct from `turnstileToken === null`, which is also the pre-solve state.
  // Only a reported failure disables the button *with an explanation*.
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const publicKey = process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY ?? "";
  const checkout = usePaystackCheckout(publicKey);

  const handleTurnstileError = (reason: TurnstileErrorReason) => {
    setTurnstileFailed(true);
    // This failure is entirely client-side: nothing reaches the server, so
    // without this event a blocked checkout is invisible in the logs.
    trackEvent("checkout_turnstile_failed", { reason });
  };

  const handleTurnstileToken = (token: string | null) => {
    setTurnstileToken(token);
    if (token) {
      setTurnstileFailed(false);
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    setError(null);

    let prepared: Awaited<ReturnType<typeof prepareWebsiteCheckout>>;
    try {
      prepared = await prepareWebsiteCheckout(processorPlanId, turnstileToken);
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to prepare your plan. Please try again."
      );
      return;
    }

    checkout.open({
      email,
      planCode: prepared.planCode,
      metadata: { plan_id: prepared.planId, flow: "create-org" },
      onSuccess: () => {
        // Cross-origin hop — router.push can't navigate to Bridge
        window.location.href = `${BRIDGE_URL}/create-org/complete`;
      },
      onCancel: () => setLoading(false),
      onError: (message) => {
        setLoading(false);
        setError(message);
      },
    });
  };

  return (
    <div className="w-full max-w-md">
      <h1 className="heading-4 mb-2 font-bold">Complete your subscription</h1>
      <p className="body-sm mb-8 text-muted-foreground">
        Payment is handled securely by Paystack.
      </p>

      {/* Order summary — same name/features as the pricing card */}
      <div className="mb-8 rounded-2xl border bg-card p-6 shadow-sm">
        <h2 className="heading-5 mb-1 font-bold">{planName}</h2>
        <p className="mb-5">
          <span className="font-bold text-3xl">{formatRand(amount)}</span>
          <span className="body-sm text-muted-foreground">
            {" "}
            {intervalLabel(interval)}
          </span>
        </p>
        <ul className="space-y-2">
          {features.map((feature) => (
            <li className="body-sm flex items-start gap-2" key={feature}>
              <Check
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-foreground"
                strokeWidth={2.5}
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <label
        className="mb-6 flex items-start gap-3 text-sm"
        htmlFor="checkout-consent"
      >
        <Checkbox
          checked={agreed}
          className="mt-0.5"
          id="checkout-consent"
          onCheckedChange={(checked) => setAgreed(checked === true)}
        />
        <span>
          I agree to the{" "}
          <Link
            className="underline underline-offset-2"
            href="/legal/terms-service"
            rel="noopener"
            target="_blank"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            className="underline underline-offset-2"
            href="/legal/privacy-policy"
            rel="noopener"
            target="_blank"
          >
            Privacy Policy
          </Link>
          .
        </span>
      </label>

      {TURNSTILE_SITE_KEY && (
        <div className="mb-4">
          <TurnstileWidget
            onError={handleTurnstileError}
            onToken={handleTurnstileToken}
            siteKey={TURNSTILE_SITE_KEY}
          />
        </div>
      )}

      {turnstileFailed && !error && (
        <p className="body-sm mb-4 text-destructive" role="alert">
          {TURNSTILE_ERROR_MESSAGE}
        </p>
      )}

      {error && (
        <p className="body-sm mb-4 text-destructive" role="alert">
          {error}
        </p>
      )}

      <Button
        className="w-full"
        disabled={
          !agreed || loading || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)
        }
        onClick={handleContinue}
        variant="fancy"
      >
        {loading ? "Preparing…" : "Continue to payment"}
      </Button>

      <p className="mt-6 text-center">
        <Link
          className="body-sm text-muted-foreground underline-offset-4 hover:underline"
          href="/partner/getting-started#getting-started_pricing"
        >
          ← Back to plans
        </Link>
      </p>
    </div>
  );
}
